import "./index.scss";
import "../../../App.scss";

import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import ProgressBar from "../ProgressBar";
import DifficultyBadge from "../DifficultyBadge";

import { selectBottomBarStarValues } from "../../../Store/store";
import { fetchImage } from "../../nonUIFuncs";

/**
 * Container component for the bottom navigation bar.
 * Provides background styling that varies based on current location.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string} props.location - Current pathname to determine styling
 * @param {Object} props.config - Application configuration object
 * @returns {JSX.Element} Bottom navigation container with background
 */
const BottomContainer = ({ children, location, config }) => {
  return (
    <div className="bottomnavigation_container">
      <div
        className={
          location === "/"
            ? "bottomnavigation_backgroundimage_welcome"
            : "bottomnavigation_backgroundimage"
        }
      />
      {children !== null ? children : null}
    </div>
  );
};

/**
 * Bottom navigation component that displays difficulty badges, progress bars, and messages.
 * Adapts content based on current route - shows difficulty selection on welcome page,
 * progress indicators on unit/game selection pages, and hides on game pages.
 *
 * @param {Object} props - Component props
 * @param {Array} props.fullStarScores - Array of star scores for each difficulty level
 * @param {Object} props.config - Application configuration object
 * @returns {JSX.Element|null} Bottom navigation appropriate for current page
 */
const BottomNavigation = ({ fullStarScores, config }) => {
  const bottomBarStarValues = useSelector(selectBottomBarStarValues);
  let routeLocation = useLocation();
  /**
   * Calculates cumulative stars for difficulty levels up to and including the specified level.
   *
   * @param {number} difficultyNum - Maximum difficulty level to include
   * @param {string} starType - Type of stars to count ('currentScore' or 'totalScore')
   * @returns {number} Total stars for the specified difficulty range
   */
  const findStars = (difficultyNum, starType) => {
    let cumulativeScores = fullStarScores.filter(
      (d) => d.difficulty <= difficultyNum,
    );
    let stars = 0;
    cumulativeScores.forEach((d) => {
      stars = stars + d[starType];
    });
    return stars;
  };
  if (routeLocation.pathname === "/") {
    let difficultyArray = [
      {
        key: 1,
        locked: false,
        currentScore: findStars(1, "currentScore") || 0,
        totalScore: findStars(1, "totalScore") || 1,
      },
      {
        key: 2,
        locked: false,
        currentScore: findStars(2, "currentScore") || 0,
        totalScore: findStars(2, "totalScore") || 1,
      },
      {
        key: 3,
        locked: false,
        currentScore: findStars(3, "currentScore") || 0,
        totalScore: findStars(3, "totalScore") || 1,
      },
      {
        key: 4,
        locked: true,
        currentScore: findStars(4, "currentScore") || 0,
        totalScore: findStars(4, "totalScore") || 1,
      },
      {
        key: 5,
        locked: true,
        currentScore: findStars(5, "currentScore") || 0,
        totalScore: findStars(5, "totalScore") || 1,
      },
    ];
    let locked = false;
    difficultyArray.map((d) => {
      let newD = d;
      newD.locked = locked;
      let percentage = (d.currentScore / d.totalScore) * 100;
      // At 75% of current highest difficulty progress, new difficulty will unlock
      if (percentage < -1) {
        locked = true;
      }
      return newD;
    });
    return (
      <BottomContainer location={routeLocation.pathname} config={config}>
        <>
          <div className="bottomnavigation_welcomecontents">
            {difficultyArray.map((dif) => {
              return (
                <Link
                  key={dif.key}
                  to={{
                    // stories live in Story Book Corner (the menu), so a
                    // level goes straight to the Discover units
                    pathname: config.hasLocationSelection ? "/choose-location" : "/choose-unit",
                    search: `difficulty=${dif.key}`,
                  }}
                  className={dif.locked === true ? "disabled_link" : ""}
                >
                  <DifficultyBadge {...dif} key={dif.key} id={dif.key} />
                </Link>
              );
            })}
          </div>
          <div
            className="bottomnavigation_message"
            style={{ backgroundColor: config.BottomColor || "black" }}
          >
            Please allow time for audio to load.
          </div>
        </>
      </BottomContainer>
    );
  } else if (
    routeLocation.pathname === "/choose-location" ||
    routeLocation.pathname === "/choose-unit" ||
    routeLocation.pathname === "/choose-game-type"
  ) {
    let difficulty = Number(
      routeLocation.search
        .replace("?", "")
        .split("&")
        .find((str) => str.includes("difficulty"))
        ?.split("=")[1],
    );
    let progress = Math.round(
      (bottomBarStarValues.currentStars / bottomBarStarValues.totalStars) * 100,
    );
    return (
      <BottomContainer location={routeLocation.pathname} config={config}>
        <div className="bottomnavigation_chooseunitcontents">
          <div
            className="bottomnavigation_chooseunitbadge"
            style={{
              backgroundImage: `url(${fetchImage(
                "Level_" + difficulty + "_Button_Base1.png",
              )})`,
            }}
          />
          <ProgressBar
            width="clamp(100px, 60vw, 220px)"
            id=""
            progress={`${progress}%`}
          />
          {/* TODO: remove lock on this display if it unlocked */}
          {difficulty <= 4 ? (
            <div
              className="bottomnavigation_chooseunitbadge"
              style={{
                backgroundImage: `url(
                    ${fetchImage(
                      "Level_" + (difficulty + 1) + "_Button_Base1.png",
                    )}
                  )`,
              }}
            >
              {progress < 70 ? (
                <div className="bottomnavigation_chooseunitbadgecover">
                  <div className="bottomnavigation_chooseunitbadgelock" />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </BottomContainer>
    );
  } else if (routeLocation.pathname.includes("/start-game")) {
    return null;
  } else {
    return (
      <BottomContainer
        children={null}
        location={routeLocation.pathname}
        config={config}
      />
    );
  }
};

export default BottomNavigation;

BottomContainer.propTypes = {
  children: PropTypes.object,
  location: PropTypes.string,
  config: PropTypes.object,
};
BottomNavigation.propTypes = {
  fullStarScores: PropTypes.array,
  config: PropTypes.object,
};
